import { Link, useLoaderData, useRouteLoaderData } from 'react-router-dom'

import { nameFor, type Lang } from '@glocal/lang'
import type { IWebsite } from '@glocal/schemas'

import { requireUser } from '../auth'
import { Chip } from '../components/chip'
import { Meter } from '../components/meter'
import { trpc } from '../trpc'

import type { loader as rootLoader } from './root'

export async function loader(): Promise<{ website_list: IWebsite[] }> {
	// React Router runs parent + child loaders in parallel. Without awaiting auth
	// here, this loader fires before Firebase finishes restoring the session,
	// and the tRPC call goes out without an Authorization header.
	await requireUser()

	const account = await trpc().AccountService.Me.query()
	const { website_list } = await trpc().AccountService.ListWebsites.query({
		account_id: account.id,
	})
	return { website_list }
}

export function Dashboard() {
	const { account } = useRouteLoaderData('root') as Awaited<ReturnType<typeof rootLoader>>
	const { website_list } = useLoaderData() as Awaited<ReturnType<typeof loader>>

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<h1 className="font-sans font-medium text-[44px] leading-[1.05] tracking-tight text-ink">Your sites</h1>
				<Link
					to="/website/new"
					state={{ accountId: account.id }}
					className="btn btn-accent"
				>
					+ Add site
				</Link>
			</div>

			{website_list.length === 0 ? (
				<EmptyState accountId={account.id} />
			) : (
				<ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
					{website_list.map((website) => (
						<li key={website.id}>
							<SiteCard website={website} />
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

/**
 * One card per site with the *next step* front and centre, so a site that
 * still needs DNS reads differently from one that's done without the owner
 * having to open it.
 *
 * "Done" is `website.verified` — a question about DNS only (at least one of
 * the site's language addresses reached active). It deliberately ignores
 * anything about what is currently switched on.
 */
export function SiteCard({ website }: { website: IWebsite }) {
	// The domain visitors use. Only a folder site fetched from a hidden origin
	// keeps it apart from `origin` — and the card names the site, not its source.
	const siteUrl = website.public ?? website.origin
	const origin = siteUrl.replace(/^https?:\/\//, '')
	const langsPicked = website.available_languages.length > 0
	const setup = !website.verified

	// Three steps to a working site: added (always done here), languages
	// picked, domain pointed. The meter shows how far along this site is.
	const progress = website.verified ? 1 : langsPicked ? 2 / 3 : 1 / 3

	const note = website.verified
		? 'All set — translations are running.'
		: langsPicked
			? 'Point your domain to finish — takes about 5 min.'
			: 'Pick languages to translate into.'

	return (
		<Link
			to={`/site/${website.id}`}
			className={
				'card flex h-full flex-col gap-3 p-5 transition-all hover:bg-paper hover:-translate-y-0.5 hover:shadow-sm ' +
				(setup ? 'border-marigold hover:border-marigold' : 'border-line hover:border-line-strong')
			}
		>
			<div className="flex items-center justify-between gap-2">
				<span className="flex min-w-0 items-center gap-2.5">
					<img
						src={`${siteUrl}/favicon.ico`}
						alt=""
						width={16}
						height={16}
						className="rounded-sm shrink-0"
						onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
					/>
					<span className="truncate font-mono text-[14px] font-medium text-ink">{origin}</span>
				</span>
				{setup ? (
					<Chip tone="preview">{langsPicked ? '1 step left' : '2 steps left'}</Chip>
				) : (
					<Chip tone="live">● Live</Chip>
				)}
			</div>

			<p className="text-[13px] text-ink-soft">
				<LangSummary website={website} />
			</p>

			<Meter
				ratio={progress}
				tone={setup ? 'accent' : 'ink'}
			/>

			<p className={'text-[12px] ' + (setup ? 'text-ember' : 'text-ink-soft')}>{note}</p>

			<span className={'btn mt-auto h-9 self-start px-4 text-[13px] ' + (setup ? 'btn-accent' : 'btn-ghost')}>
				{setup ? 'Finish setup →' : 'Open →'}
			</span>
		</Link>
	)
}

const SUMMARY_LIMIT = 3

function LangSummary({ website }: { website: IWebsite }) {
	if (website.available_languages.length === 0) {
		return <>No languages picked yet</>
	}
	const names = website.available_languages.map((l) => nameFor(l) ?? l.toUpperCase())
	const extra = names.length - SUMMARY_LIMIT
	return (
		<>
			{langLabel(website.lang)} → {names.slice(0, SUMMARY_LIMIT).join(', ')}
			{extra > 0 && ` + ${extra} more`}
		</>
	)
}

function langLabel(code: string): string {
	return nameFor(code as Lang) ?? code.toUpperCase()
}

export function EmptyState({ accountId }: { accountId: string }) {
	return (
		<div className="rounded-nayda-lg border border-dashed border-line-strong p-12 text-center">
			<div className="text-[48px] mb-2">🐕</div>
			<h2 className="font-sans text-[24px] font-medium text-ink">No sites yet</h2>
			<p className="mx-auto mt-1 max-w-md text-[15px] text-ink-soft">
				Add your first site — about 2 minutes, no code needed. Every language you pick gets a free preview link
				right away.
			</p>
			<Link
				to="/website/new"
				state={{ accountId }}
				className="btn btn-accent mt-6"
			>
				+ Add your first site
			</Link>
		</div>
	)
}
